import React, {
    FunctionComponent,
    ReactElement,
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    Chip,
    CircularProgress,
    TextField,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";

import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridRowParams,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { format } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";

import { EmptyRecords } from "../../components/common";
import { DataGridFooter } from "../../components/common";
import { useGetAllCarts } from "../../hooks/useGetAllCarts";
import { IPage } from "../../types";

const Root = styled("main")`
    padding: ${({ theme }) => theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SearchField = styled(TextField)`
    width: 300px;
`;

const SearchContainer = styled("div")`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const EmptyRecordsContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
`;

const StyledDataGrid = styled(DataGrid)`
    .MuiDataGrid-cell:focus {
        outline: none;
    }
`;

const CellTitle = styled(Typography)`
    font-size: 16px;
    color: ${({ theme }) => theme.palette.text.primary};
`;

const Description = styled(Typography)`
    font-size: 13px;
    color: grey;
`;

const StyledCell = styled("div")`
    display: flex;
    flex-direction: column;
    align-items: start;
`;

const ItemsCell = styled("div")`
    display: flex;
    gap: ${({ theme }) => theme.spacing(0.5)};
    align-items: center;
    font-size: 13px;
`;

const getStatusColor = (status: string | number) => {
    const statusStr = status.toString();
    switch (statusStr) {
        case "1":
        case "ACTIVE":
            return "success";
        case "4":
        case "ADMIN_ACTIVE":
            return "primary";
        case "5":
        case "FROZEN":
            return "warning";
        case "3":
        case "ORDER_CONVERTED":
            return "info";
        case "2":
        case "MERGED":
            return "secondary";
        default:
            return "default";
    }
};

const getStatusLabel = (status: string | number) => {
    const statusMap: Record<string, string> = {
        "1": "Active",
        "2": "Merged",
        "3": "Order Converted",
        "4": "Admin Active",
        "5": "Frozen",
        "ACTIVE": "Active",
        "MERGED": "Merged",
        "ORDER_CONVERTED": "Order Converted",
        "ADMIN_ACTIVE": "Admin Active",
        "FROZEN": "Frozen",
    };
    return statusMap[status.toString()] || status.toString();
};

const getStatusTooltip = (status: string | number) => {
    const tooltipMap: Record<string, string> = {
        "1": "The cart is active and can be modified by the customer. Carts under ACTIVE status are currently not accessible to admins.",
        "2": "The cart has been merged with another cart.",
        "3": "The cart has been converted into an order.",
        "4": "The cart is active and can be modified by the admin who created it. Carts under ADMIN_ACTIVE status are inaccessible to customers.",
        "5": "Carts under FROZEN status cannot be modified. In the admin order flow, a cart is marked as FROZEN once the create order button is pressed.",
        "ACTIVE": "The cart is active and can be modified by the customer. Carts under ACTIVE status are currently not accessible to admins.",
        "MERGED": "The cart has been merged with another cart.",
        "ORDER_CONVERTED": "The cart has been converted into an order.",
        "ADMIN_ACTIVE": "The cart is active and can be modified by the admin who created it. Carts under ADMIN_ACTIVE status are inaccessible to customers.",
        "FROZEN": "Carts under FROZEN status cannot be modified. In the admin order flow, a cart is marked as FROZEN once the create order button is pressed.",
    };
    return tooltipMap[status.toString()] || "";
};

const columns: GridColDef[] = [
    {
        field: "customer",
        headerName: "Customer",
        flex: 0.2,
        minWidth: 180,
        renderCell: (params: GridRenderCellParams) => (
            <StyledCell>
                <CellTitle>
                    {params.row.customer?.firstName} {params.row.customer?.lastName}
                </CellTitle>
                <Description>{params.row.customer?.email}</Description>
            </StyledCell>
        ),
    },
    {
        field: "phoneNumber",
        headerName: "Phone",
        flex: 0.15,
        minWidth: 120,
        valueGetter: (params: GridValueGetterParams) =>
            params.row.customer?.phoneNumber || "N/A",
    },
    {
        field: "status",
        headerName: "Status",
        flex: 0.15,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams) => (
            <Tooltip title={getStatusTooltip(params.row.status)}>
                <Chip
                    label={getStatusLabel(params.row.status)}
                    color={getStatusColor(params.row.status)}
                    size="small"
                />
            </Tooltip>
        ),
    },
    {
        field: "items",
        headerName: "Items",
        flex: 0.3,
        minWidth: 200,
        valueGetter: (params: GridValueGetterParams) => {
            const items = params.row.items || [];
            const firstItem = items[0];
            const remainingCount = items.length - 1;
            return { firstItem, remainingCount, allItems: items };
        },
        renderCell: (params: GridRenderCellParams<{ firstItem: any; remainingCount: number; allItems: any[] }>) => (
            <ItemsCell>
                {params.value?.firstItem && (
                    <Tooltip title={params.value.firstItem.title || 'Product'}>
                        <Chip 
                            label={`${params.value.firstItem.title?.split(' - ')[0] || 'Product'} (${params.value.firstItem.quantity})`} 
                            size="small" 
                            variant="outlined"
                        />
                    </Tooltip>
                )}
                {(params.value?.remainingCount || 0) > 0 && (
                    <Tooltip title={params.value?.allItems?.slice(1).map((item: any) => `${item.title} (${item.quantity})`).join(', ')}>
                        <Chip
                            label={`+${params.value?.remainingCount}`}
                            size="small"
                            variant="outlined"
                        />
                    </Tooltip>
                )}
            </ItemsCell>
        ),
    },
    {
        field: "totalSellingPrice",
        headerName: "Total Amount",
        flex: 0.15,
        minWidth: 120,
        valueGetter: (params: GridValueGetterParams) => 
            `₹${params.row.totalSellingPrice?.toFixed(2) || '0.00'}`,
    },
    {
        field: "createdAt",
        headerName: "Created",
        flex: 0.1,
        minWidth: 120,
        valueGetter: (params: GridValueGetterParams) =>
            format(new Date(params.row.createdAt), "dd MMM yyyy"),
    },
    {
        field: "updatedAt",
        headerName: "Last updated",
        flex: 0.1,
        minWidth: 120,
        valueGetter: (params: GridValueGetterParams) =>
            format(new Date(params.row.updatedAt), "dd MMM yyyy"),
    },
];

export const CartsScreen: FunctionComponent = (): ReactElement => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchInput, setSearchInput] = useState<string>(
        searchParams.get("search") || "",
    );
    const [searchQuery, setSearchQuery] = useState<string>(
        searchParams.get("search") || "",
    );
    const [page, setPage] = useState<IPage>({
        limit: 20,
        cursor: null,
        direction: "after",
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQuery(searchInput);
            if (searchInput) {
                setSearchParams({ search: searchInput });
            } else {
                setSearchParams({});
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchInput, setSearchParams]);

    const fetchAllCarts = useGetAllCarts(
        [
            "carts",
            page.limit.toString(),
            page.cursor ?? "",
            page.direction,
            searchQuery,
        ],
        {
            limit: page.limit,
            cursor: page.cursor,
            direction: page.direction,
            search: searchQuery || undefined,
        },
    );

    const handleRowClick = useCallback(
        (params: GridRowParams) => {
            const cartId = params.row.id;
            navigate(`/carts/${cartId}`);
        },
        [navigate],
    );

    const handleNavigateBack = useCallback(() => {
        setPage({
            ...page,
            cursor: fetchAllCarts.data?.cursors?.previous ?? "",
            direction: "before",
        });
    }, [fetchAllCarts.data?.cursors?.previous, page]);

    const handleNavigateForward = useCallback(() => {
        setPage({
            ...page,
            cursor: fetchAllCarts.data?.cursors?.next ?? "",
            direction: "after",
        });
    }, [fetchAllCarts.data?.cursors?.next, page]);

    return (
        <Root>
            <TitleContainer>
                <Title>Carts</Title>
                <SearchContainer>
                    <SearchField
                        size="small"
                        placeholder="Search by customer name or phone"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                    />
                    {fetchAllCarts.isLoading && <CircularProgress size={20} />}
                </SearchContainer>
            </TitleContainer>

            {fetchAllCarts?.data?.records.length === 0 && (
                <EmptyRecords>
                    <EmptyRecordsContainer>
                        <Title>No carts found</Title>
                        <div>
                            Carts will appear here when customers add items to their cart.
                        </div>
                    </EmptyRecordsContainer>
                </EmptyRecords>
            )}

            {fetchAllCarts.data &&
                (fetchAllCarts.data?.records ?? []).length > 0 && (
                    <StyledDataGrid
                        rows={fetchAllCarts?.data?.records ?? []}
                        columns={columns}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 20,
                                },
                            },
                        }}
                        pageSizeOptions={[10, 20, 30]}
                        disableRowSelectionOnClick={true}
                        slots={{
                            footer: () => (
                                <DataGridFooter
                                    hasNextRecords={Boolean(
                                        fetchAllCarts.data.cursors.next,
                                    )}
                                    hasPreviousRecords={Boolean(
                                        fetchAllCarts.data.cursors.previous,
                                    )}
                                    onNavigateBack={handleNavigateBack}
                                    onNavigateForward={handleNavigateForward}
                                />
                            ),
                        }}
                    />
                )}
        </Root>
    );
};